import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { filter, firstValueFrom, map, switchMap } from 'rxjs';
import {
  ImageUploaderComponent,
  UploadedImage,
} from '@components/app-image-uploader/image-uploader.component';

import { CategoriesService } from '@services/categories/categories.service';
import { Category } from '@services/categories/interfaces/categories.interfaces';
import { ColorsService } from '@services/colors/colors.service';
import { MaterialColor } from '@services/colors/interfaces/colors.interfaces';
import { ModalService } from '@services/components/ModalService/modal.service';
import { ToastService } from '@services/components/ToastService/toast.service';
import { Product } from '@services/products/interfaces/product.interfaces';
import { ProductColorDetail, ProductDetail, ProductImageShorter, ProductSizeDetail, UpdateProductDto, UpdateProductSizeDto } from '@services/products/interfaces/product-detail.interfaces';
import { ProductService } from '@services/products/products.service';
import { NullableShippingDimensions, ShippingBox, ShippingDimensions } from '@services/shipping/interfaces/shipping.interfaces';
import { ShippingService } from '@services/shipping/shipping.service';
import {
  GenericConfirmModalComponent,
  GenericConfirmModalData,
} from '@shared/components/UI/generic-confirm-modal/generic-confirm-modal.component';
import { GenericErrorModalComponent } from '@shared/components/UI/generic-error-modal/generic-error-modal.component';
import { DELETE_PRODUCT_CONFIRM_MODAL } from '@shared/constants/modal.constants';
import { buildSuccessProductToast, SUCCESS_DELETE_PRODUCT_TOAST } from '@shared/constants/toasts.constants';
import { getBoxFitProblem } from '@shared/utils/shipping-box.utils';

import { CDN_PRODUCTS_URL } from '@constants/constants';
import { MERCHANDISING_CATEGORY_ID } from '@constants/merchandising.constants';
import { ADMIN_PRODUCTS_DETAILS } from '@constants/routes.constants';

const SIZE_LABEL_PATTERN = /^\d+(\.\d+)?(mm|cm|m)$/;
const BASE_PRICE_SIZE_LABEL = '0cm';
const SHIPPING_DIMENSION_MAX_CM = 200;
const SHIPPING_WEIGHT_MAX_GRAMS = 1000000;
const PICKUP_ONLY_CONTROL = 'isPickupOnly';
const SHIPPING_DIMENSION_FIELDS: Array<{ key: keyof ShippingDimensions; label: string }> = [
  { key: 'weightGrams', label: 'peso' },
  { key: 'lengthCm', label: 'largo' },
  { key: 'widthCm', label: 'ancho' },
  { key: 'heightCm', label: 'alto' },
];

@Component({
  selector: 'app-product-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageUploaderComponent,
  ],
  templateUrl: './product-form.html',
  styleUrls: ['./product-form.css'],
})
export class ProductFormComponent implements OnInit {
  @ViewChild('basePriceInput') private basePriceInputRef?: ElementRef<HTMLInputElement>;
  @ViewChildren('sizePriceInput') private sizePriceInputRefs!: QueryList<ElementRef<HTMLInputElement>>;

  public productForm!: FormGroup;
  public readonly merchandisingCategoryId = MERCHANDISING_CATEGORY_ID;
  public isSubmitting = signal(false);
  public loadingCategories = signal(false);
  public loadingColors = signal(false);
  public mainCategories = signal<Category[]>([]);
  public subCategories = signal<Category[]>([]);
  private allCategories = signal<Category[]>([]);
  public activeShippingBoxes = signal<ShippingBox[]>([]);
  public availableColors = signal<MaterialColor[]>([]);
  public previewImages = signal<string[]>([]);
  public existingImageUrls = signal<Array<{ url: string; fileSize: number | null }>>([]);
  public finalImagesToSubmit: File[] = [];

  private isLoadingProductData = false;
  public editingProductId: string | null = null;
  private originalImages: ProductImageShorter[] = [];
  private currentImages: UploadedImage[] = [];
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private categoriesService = inject(CategoriesService);
  private productService = inject(ProductService);
  private shippingService = inject(ShippingService);
  private colorsService = inject(ColorsService);
  private modalService = inject(ModalService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  public get baseDimensionsGroup(): FormGroup {
    return this.productForm.get('baseDimensions') as FormGroup;
  }

  public get sizesFormArray(): FormArray {
    return this.productForm.get('sizes') as FormArray;
  }

  public get colorsFormArray(): FormArray {
    return this.productForm.get('colors') as FormArray;
  }

  public get useSizeVariations(): boolean {
    return !!this.productForm?.get('useSizeVariations')?.value;
  }

  public get hasImageChanges(): boolean {
    if (this.currentImages.some((img) => img.file !== null)) {
      return true;
    }

    const remainingExistingUrls = this.currentImages.map((img) => img.url);
    if (remainingExistingUrls.length !== this.originalImages.length) {
      return true;
    }

    return this.originalImages.some((orig, idx) => orig.url !== remainingExistingUrls[idx]);
  }

  public get canSubmit(): boolean {
    if (this.isSubmitting() || this.productForm?.invalid) {
      return false;
    }

    if (!this.editingProductId) {
      return true;
    }

    return this.productForm.dirty || this.hasImageChanges;
  }

  public get formErrors(): string[] {
    const errors: string[] = [];
    if (!this.productForm) {
      return errors;
    }

    if (this.currentImages.length === 0) {
      errors.push('El producto debe tener al menos una foto.');
    }

    if (this.productForm.get('name')?.errors?.['required']) {
      errors.push('El nombre del producto es requerido.');
    }
    if (this.productForm.get('categoryId')?.errors?.['required']) {
      errors.push('La categoría es requerida.');
    }

    if (!this.useSizeVariations) {
      const basePriceErrors = this.productForm.get('basePrice')?.errors;
      if (basePriceErrors?.['required']) {
        errors.push('El precio base es requerido.');
      } else if (basePriceErrors?.['min']) {
        errors.push('El precio base debe ser mayor a 0.');
      }

      errors.push(...this.getDimensionErrors(this.baseDimensionsGroup, 'Envío'));
    }

    if (this.useSizeVariations) {
      this.sizesFormArray.controls.forEach((ctrl, index) => {
        const position = index + 1;
        const labelErrors = ctrl.get('label')?.errors;
        const priceErrors = ctrl.get('price')?.errors;

        if (labelErrors?.['required']) {
          errors.push(`Variación de tamaño #${position}: el tamaño está vacío.`);
        } else if (labelErrors?.['pattern']) {
          errors.push(
            `Variación de tamaño #${position}: formato inválido, debe terminar en 'cm', 'mm' o 'm' (ej: 1cm, 0.5cm, 1.5m).`,
          );
        }

        if (priceErrors?.['required']) {
          errors.push(`Variación de tamaño #${position}: el precio es requerido.`);
        } else if (priceErrors?.['min']) {
          errors.push(`Variación de tamaño #${position}: el precio debe ser mayor a 0.`);
        }

        errors.push(...this.getDimensionErrors(ctrl as FormGroup, `Variación de tamaño #${position}`));
      });
    }

    return errors;
  }

  ngOnInit(): void {
    this.initForm();

    this.activatedRoute.paramMap
      .pipe(
        map((params) => params.get('slug')),
        filter((slug): slug is string => !!slug),
        switchMap((slug) => this.productService.getProductBySlugAdmin(slug)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (product) => {
          if (product) {
            this.loadProductData(product);
          } else {
            this.handleError();
          }
        },
        error: () => this.handleError(),
      });

    this.loadMainCategories();
    this.loadAvailableColors();
    this.loadActiveShippingBoxes();
    this.listenToCategoryChanges();
  }

  public addSize(): void {
    this.sizesFormArray.push(this.createSizeGroup());
  }

  public removeSize(index: number): void {
    if (this.sizesFormArray.length <= 1) {
      return;
    }

    this.sizesFormArray.removeAt(index);
    this.productForm.markAsDirty();
  }

  public addColor(): void {
    this.colorsFormArray.push(this.createColorGroup());
    this.cdr.detectChanges();
  }

  public removeColor(index: number): void {
    if (this.colorsFormArray.length <= 1) {
      return;
    }

    this.colorsFormArray.removeAt(index);
    this.productForm.markAsDirty();
  }

  public onPriceInput(event: Event, controlPath: string | (string | number)[]): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    const numeric = digits ? parseInt(digits, 10) : null;
    this.productForm.get(controlPath)?.setValue(numeric, { emitEvent: false });
    input.value = this.formatClp(numeric);
  }

  public onPriceBlur(event: Event, controlPath: string | (string | number)[]): void {
    const input = event.target as HTMLInputElement;
    const numeric = this.productForm.get(controlPath)?.value ?? null;
    input.value = this.formatClp(numeric);
    input.blur();
  }

  public onImagesUpdated(images: UploadedImage[]): void {
    this.currentImages = images;
    this.finalImagesToSubmit = images
      .map((img) => img.file)
      .filter((file): file is File => file !== null);
    this.previewImages.set(images.map((img) => img.url));
  }

  public onCancel(): void {
    this.resetToEmptyForm();
    this.router.navigate([ADMIN_PRODUCTS_DETAILS]);
  }

  public onClearFormClick(): void {
    this.modalService
      .openModal<boolean, GenericConfirmModalData>({
        title: 'Confirmar acción',
        component: GenericConfirmModalComponent,
        data: {
          message: '¿Estás seguro de limpiar el formulario?',
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.reason === 'confirm') {
          this.resetToEmptyForm();
        }
      });
  }

  public onDeleteProductClick(): void {
    if (!this.editingProductId) {
      return;
    }

    const productId = this.editingProductId;

    this.modalService
      .openModal<boolean, GenericConfirmModalData>(DELETE_PRODUCT_CONFIRM_MODAL)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.reason === 'confirm') {
          this.productService.deleteProduct(productId).subscribe({
            next: () => {
              this.toastService.open(SUCCESS_DELETE_PRODUCT_TOAST);
              this.router.navigate([ADMIN_PRODUCTS_DETAILS]);
            },
            error: () => this.handleError(),
          });
        }
      });
  }

  public onSubmit(): void {
    if (this.productForm.invalid || this.currentImages.length === 0 || this.hasIncompleteShippingDimensions()) {
      this.productForm.markAllAsTouched();
      return;
    }

    const isEditing = !!this.editingProductId;
    this.modalService
      .openModal<boolean, GenericConfirmModalData>({
        title: 'Confirmar acción',
        component: GenericConfirmModalComponent,
        data: {
          message: isEditing
            ? '¿Estás seguro de guardar los cambios?'
            : '¿Estás seguro de crear este producto?',
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.reason === 'confirm') {
          this.submitProduct();
        }
      });
  }

  private submitProduct(): void {
    this.isSubmitting.set(true);

    if (this.editingProductId) {
      this.submitEdit(this.editingProductId);
      return;
    }

    const formData = this.createFormData();
    this.productService.createProduct(formData).subscribe({
      next: (response) => this.handleSubmitSuccess(response),
      error: () => this.handleSubmitError(),
    });
  }

  private submitEdit(id: string): void {
    const body = this.buildUpdateDto();
    const imageFormData = this.buildImagesFormData();

    this.productService.updateProduct(id, body).subscribe({
      next: () => {
        if (!imageFormData) {
          this.handleSubmitSuccess(null);
          return;
        }
        this.productService.updateProductImages(id, imageFormData).subscribe({
          next: () => this.handleSubmitSuccess(null),
          error: () => this.handleSubmitError(),
        });
      },
      error: () => this.handleSubmitError(),
    });
  }

  private buildUpdateDto(): UpdateProductDto {
    const formValues = this.productForm.getRawValue();
    const sizes = this.buildSizesPayload(formValues);
    const colorIds = (formValues.colors ?? [])
      .map((color: { colorId: string }) => color.colorId)
      .filter((id: string) => !!id && id !== 'none');

    return {
      name: formValues.name,
      description: formValues.description,
      status: formValues.status,
      mainCategoryId: formValues.categoryId,
      subCategoryId: formValues.subCategoryId || null,
      basePrice: sizes[0]?.price ?? 0,
      sizes,
      colorIds,
    };
  }

  private buildSizesPayload(
    formValues: {
      useSizeVariations: boolean;
      basePrice: number;
      baseDimensions: Record<keyof ShippingDimensions | typeof PICKUP_ONLY_CONTROL, unknown>;
      sizes: Array<{ label: string; price: number } & Record<keyof ShippingDimensions | typeof PICKUP_ONLY_CONTROL, unknown>>;
    },
  ): UpdateProductSizeDto[] {
    if (formValues.useSizeVariations) {
      return (formValues.sizes ?? []).map((size) => ({
        label: size.label,
        price: Number(size.price) || 0,
        ...this.toShippingDimensions(size),
      }));
    }

    return [{
      label: BASE_PRICE_SIZE_LABEL,
      price: Number(formValues.basePrice) || 0,
      ...this.toShippingDimensions(formValues.baseDimensions),
    }];
  }

  private buildImagesFormData(): FormData | null {
    const remainingExistingIds: string[] = [];
    const originalById = new Map(this.originalImages.map((img) => [img.url, img.id]));

    for (const img of this.currentImages) {
      if (img.file !== null) {
        continue;
      }
      const id = originalById.get(img.url);
      if (id) {
        remainingExistingIds.push(id);
      }
    }

    const removeImageIds = this.originalImages
      .filter((orig) => !remainingExistingIds.includes(orig.id))
      .map((orig) => orig.id);

    const originalOrder = this.originalImages.map((img) => img.id);
    const survivingOriginalOrder = originalOrder.filter((id) => remainingExistingIds.includes(id));
    const reordered = remainingExistingIds.some((id, idx) => survivingOriginalOrder[idx] !== id);

    const hasNewFiles = this.finalImagesToSubmit.length > 0;
    const hasRemovals = removeImageIds.length > 0;

    if (!hasNewFiles && !hasRemovals && !reordered) {
      return null;
    }

    const formData = new FormData();
    this.finalImagesToSubmit.forEach((file) => formData.append('images', file));

    if (hasRemovals) {
      formData.append('removeImageIds', JSON.stringify(removeImageIds));
    }
    if (reordered) {
      formData.append('reorderImageIds', JSON.stringify(remainingExistingIds));
    }

    return formData;
  }

  public getAvailableColorsForRow(index: number): MaterialColor[] {
    const selectedIds = this.colorsFormArray.controls
      .map((ctrl, i) => (i === index ? null : ctrl.get('colorId')?.value))
      .filter((id): id is string => !!id);

    return this.availableColors()
      .filter((color) => !selectedIds.includes(color.id));
  }

  public getColorHex(colorId: string): string {
    const color = this.availableColors()
      .find((color) => color.id === colorId);

    return color?.primaryHex || '#ffffff';
  }

  public getColorSwatchStyle(colorId: string): { [klass: string]: string } {
    const color = this.availableColors().find((c) => c.id === colorId);
    if (!color) {
      return { backgroundColor: '#ffffff' };
    }

    const hexes = [color.primaryHex, color.secondaryHex, color.tertiaryHex].filter(Boolean) as string[];
    if (!hexes.length) {
      return { backgroundColor: '#ffffff' };
    }

    if (hexes.length === 1) {
      return { backgroundColor: hexes[0] };
    }

    if (hexes.length === 2) {
      return { background: `linear-gradient(135deg, ${hexes[0]} 50%, ${hexes[1]} 50%)` };
    }

    return {
      background: `linear-gradient(135deg, ${hexes[0]} 33.33%, ${hexes[1]} 33.33%, ${hexes[1]} 66.66%, ${hexes[2]} 66.66%)`,
    };
  }

  public getColorIsAvailable(colorId: string): boolean {
    const color = this.availableColors()
      .find((color) => color.id === colorId);

    return color?.isAvailable ?? true;
  }

  public isProductOutOfStock(): boolean {
    const selectedColorIds = this.colorsFormArray.controls
      .map((ctrl) => ctrl.get('colorId')?.value)
      .filter((id): id is string => !!id && id !== 'none');

    if (selectedColorIds.length === 0) {
      return this.productForm?.get('status')?.value !== 'active';
    }

    return selectedColorIds.some((id) => !this.getColorIsAvailable(id));
  }

  public isPickupOnly(dimensionsGroup: FormGroup): boolean {
    return Boolean(dimensionsGroup.get(PICKUP_ONLY_CONTROL)?.value);
  }

  public getShippingFitWarning(dimensionsGroup: FormGroup): string | null {
    if (this.isPickupOnly(dimensionsGroup)) {
      return null;
    }

    const { weightGrams, lengthCm, widthCm, heightCm } = dimensionsGroup.getRawValue() as Record<keyof ShippingDimensions, unknown>;
    const dimensions = {
      weightGrams: Number(weightGrams),
      lengthCm: Number(lengthCm),
      widthCm: Number(widthCm),
      heightCm: Number(heightCm),
    };

    if (Object.values(dimensions).some((value) => !Number.isFinite(value) || value <= 0)) {
      return null;
    }

    return getBoxFitProblem(dimensions, this.activeShippingBoxes());
  }

  public applyPickupOnlyState(dimensionsGroup: FormGroup): void {
    const shouldDisableDimensions = this.isPickupOnly(dimensionsGroup) || Boolean(dimensionsGroup.get(PICKUP_ONLY_CONTROL)?.disabled);

    SHIPPING_DIMENSION_FIELDS.forEach(({ key }) => {
      const dimensionControl = dimensionsGroup.get(key);
      if (shouldDisableDimensions) {
        dimensionControl?.disable({ emitEvent: false });
      } else {
        dimensionControl?.enable({ emitEvent: false });
      }
    });
  }

  private initForm(): void {
    this.productForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      categoryId: ['', Validators.required],
      subCategoryId: [''],
      status: ['active', Validators.required],
      useSizeVariations: [false],
      basePrice: [null, [Validators.required, Validators.min(1)]],
      baseDimensions: this.createDimensionsGroup(),
      sizes: this.fb.array([], Validators.minLength(1)),
      colors: this.fb.array([], Validators.minLength(1)),
    });
    this.addSize();
    this.addColor();
    this.applySizeVariationsMode(false);

    this.productForm
      .get('useSizeVariations')
      ?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled: boolean) => this.applySizeVariationsMode(enabled));
  }

  private applySizeVariationsMode(enabled: boolean): void {
    const basePriceCtrl = this.productForm.get('basePrice');
    if (enabled) {
      basePriceCtrl?.disable({ emitEvent: false });
      this.baseDimensionsGroup.disable({ emitEvent: false });
      this.sizesFormArray.enable({ emitEvent: false });
      this.sizesFormArray.controls.forEach((sizeControl) => this.applyPickupOnlyState(sizeControl as FormGroup));
    } else {
      basePriceCtrl?.enable({ emitEvent: false });
      this.baseDimensionsGroup.enable({ emitEvent: false });
      this.applyPickupOnlyState(this.baseDimensionsGroup);
      this.sizesFormArray.disable({ emitEvent: false });
    }
  }

  private loadMainCategories(): void {
    this.loadingCategories.set(true);

    this.categoriesService
      .getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (categories: Category[]) => this.setMainCategories(categories),
        error: () => {
          this.mainCategories.set([]);
          this.loadingCategories.set(false);
          this.handleError();
        },
      });
  }

  private setMainCategories(categories: Category[]): void {
    this.allCategories.set(categories);
    const mainCategories = categories.filter((category) => !category.parentId);
    this.mainCategories.set(mainCategories);
    this.loadingCategories.set(false);
  }

  private listenToCategoryChanges(): void {
    this.productForm
      .get('categoryId')
      ?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categoryId: string) => {
        if (!this.isLoadingProductData) {
          this.productForm.get('subCategoryId')?.setValue('', { emitEvent: false });
          this.subCategories.set([]);
        }

        if (!categoryId) {
          return;
        }

        const subs = this.allCategories().filter((cat) => cat.parentId === categoryId);
        this.subCategories.set(subs);
      });
  }

  private loadActiveShippingBoxes(): void {
    this.shippingService
      .getShippingBoxes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ boxes }) => this.activeShippingBoxes.set(boxes.filter((box) => box.isActive)),
        error: () => this.activeShippingBoxes.set([]),
      });
  }

  private loadAvailableColors(): void {
    this.loadingColors.set(true);
    this.colorsService
      .getColorsAdmin()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (colors) => {
          this.availableColors.set(colors);
          this.loadingColors.set(false);
        },
        error: () => {
          this.loadingColors.set(false);
          this.handleError();
        },
      });
  }

  private async loadProductData(fullProductData: ProductDetail): Promise<void> {
    this.isLoadingProductData = true;

    try {
      this.editingProductId = fullProductData.id;

      const allCategories = await firstValueFrom(
        this.categoriesService.getCategories(),
      ).catch(() => [] as Category[]);

      const subs = allCategories.filter((cat) => cat.parentId === fullProductData.mainCategory);
      this.subCategories.set(subs);

      this.productForm.patchValue(
        {
          name: fullProductData.name || '',
          description: fullProductData.description || '',
          categoryId: fullProductData.mainCategory,
          subCategoryId: fullProductData.subCategory,
          status: fullProductData.status || 'active',
          basePrice: fullProductData.basePrice ?? null,
        },
        { emitEvent: false },
      );

      this.loadSizes(fullProductData.sizes || [], fullProductData.baseShippingDimensions ?? null);
      this.loadColors(fullProductData.colors || []);
      this.loadImages(fullProductData.productImages || []);
      this.cdr.detectChanges();
      this.formatBasePriceInput(this.productForm.get('basePrice')?.value ?? null);
      this.formatSizePriceInputs();
    } catch {
      this.handleError();
    } finally {
      this.isLoadingProductData = false;
    }
  }

  private loadSizes(sizes: ProductSizeDetail[], baseShippingDimensions: NullableShippingDimensions | null): void {
    this.sizesFormArray.clear();
    this.patchDimensionsGroup(this.baseDimensionsGroup, baseShippingDimensions);

    const isBasePriceMode = sizes.length === 1 && sizes[0]?.label === BASE_PRICE_SIZE_LABEL;

    if (isBasePriceMode) {
      this.productForm.get('basePrice')?.setValue(sizes[0].price ?? 0, { emitEvent: false });
      this.patchDimensionsGroup(this.baseDimensionsGroup, sizes[0]);
      this.productForm.get('useSizeVariations')?.setValue(false, { emitEvent: false });
      this.addSize();
      this.applySizeVariationsMode(false);
      return;
    }

    if (!sizes.length) {
      this.addSize();
      this.productForm.get('useSizeVariations')?.setValue(false, { emitEvent: false });
      this.applySizeVariationsMode(false);
      return;
    }

    sizes.forEach((sizeData) => {
      this.sizesFormArray.push(
        this.fb.group({
          label: [
            sizeData.label ?? '',
            [Validators.required, Validators.pattern(SIZE_LABEL_PATTERN)],
          ],
          price: [
            sizeData.price ?? 0,
            [Validators.required, Validators.min(1)],
          ],
          ...this.createDimensionControls(sizeData),
        }),
      );
    });

    this.productForm.get('useSizeVariations')?.setValue(true, { emitEvent: false });
    this.applySizeVariationsMode(true);
    this.sizesFormArray.updateValueAndValidity();
  }

  private loadColors(colors: ProductColorDetail[]): void {
    this.colorsFormArray.clear();

    if (!colors.length) {
      this.addColor();
      return;
    }

    colors.forEach((colorData) => {
      this.colorsFormArray.push(
        this.fb.group({
          colorId: [colorData.id ?? '', Validators.required],
        }),
      );
    });

    this.colorsFormArray.updateValueAndValidity();
  }

  private loadImages(images: ProductImageShorter[]): void {
    if (!images.length) {
      this.originalImages = [];
      this.currentImages = [];
      return;
    }

    const sorted = [...images].sort((a, b) => a.displayOrder - b.displayOrder);
    const resolved = sorted.map((img) => {
      const rawUrl = img?.url ?? '';
      const url = !rawUrl
        ? ''
        : /^https?:\/\//.test(rawUrl) ? rawUrl : `${CDN_PRODUCTS_URL}${rawUrl}`;
      return { id: img.id, url, fileSize: img.fileSize ?? null, displayOrder: img.displayOrder };
    });

    this.originalImages = resolved;
    this.currentImages = resolved.map((img) => ({ file: null, url: img.url, fileSize: img.fileSize }));

    this.existingImageUrls.set(resolved.map((img) => ({ url: img.url, fileSize: img.fileSize })));
    this.previewImages.set(resolved.map((img) => img.url));
  }

  private createSizeGroup(): FormGroup {
    return this.fb.group({
      label: ['', [Validators.required, Validators.pattern(SIZE_LABEL_PATTERN)]],
      price: [null, [Validators.required, Validators.min(1)]],
      ...this.createDimensionControls(null),
    });
  }

  private createColorGroup(): FormGroup {
    return this.fb.group({
      colorId: ['none'],
    });
  }

  private resetToEmptyForm(): void {
    this.productForm.reset({
      name: '',
      description: '',
      categoryId: '',
      subCategoryId: '',
      status: 'active',
      useSizeVariations: false,
      basePrice: null,
      baseDimensions: {},
    });

    this.sizesFormArray.clear();
    this.colorsFormArray.clear();
    this.addSize();
    this.addColor();
    this.applySizeVariationsMode(false);
    this.previewImages.set([]);
    this.existingImageUrls.set([]);
    this.finalImagesToSubmit = [];
    this.originalImages = [];
    this.currentImages = [];
    this.editingProductId = null;
    this.productForm.markAsPristine();
    this.productForm.markAsUntouched();
    this.formatBasePriceInput(null);
  }

  private createFormData(): FormData {
    const formValues = this.productForm.getRawValue();
    const formData = new FormData();

    formData.append('name', formValues.name);
    formData.append('description', formValues.description);
    formData.append('status', formValues.status);
    formData.append('mainCategoryId', formValues.categoryId);
    formData.append('isNew', 'true');

    const sizes = this.buildSizesPayload(formValues);
    const firstPrice = sizes[0]?.price || 0;
    formData.append('basePrice', firstPrice.toString());
    formData.append('sizes', JSON.stringify(sizes));

    const colorIds = formValues.colors
      .map((color: { colorId: string }) => color.colorId)
      .filter((id: string) => id && id !== 'none');
    formData.append('colorIds', JSON.stringify(colorIds));

    this.finalImagesToSubmit.forEach((file) => {
      formData.append('images', file);
    });
    return formData;
  }

  private handleSubmitSuccess(_response: Product | null): void {
    this.isSubmitting.set(false);
    this.toastService.open(buildSuccessProductToast(!!this.editingProductId));
    this.router.navigate([ADMIN_PRODUCTS_DETAILS]);
  }

  private handleSubmitError(): void {
    this.isSubmitting.set(false);
    this.handleError();
  }

  private formatClp(value: number | null): string {
    return value !== null ? `$ ${value.toLocaleString('es-CL', { useGrouping: true })}` : '';
  }

  private formatSizePriceInputs(): void {
    this.sizePriceInputRefs?.forEach((ref, index) => {
      const numeric = this.sizesFormArray.at(index)?.get('price')?.value ?? null;
      ref.nativeElement.value = this.formatClp(numeric);
    });
  }

  private formatBasePriceInput(value: number | null): void {
    if (!this.basePriceInputRef) {
      return;
    };
    this.basePriceInputRef.nativeElement.value = this.formatClp(value);
  }

  private handleError(): void {
    this.modalService.openModal({
      title: 'Error del servidor',
      component: GenericErrorModalComponent,
    });
  }

  private createDimensionsGroup(): FormGroup {
    return this.fb.group(this.createDimensionControls(null));
  }

  private createDimensionControls(
    dimensions: Partial<NullableShippingDimensions> | null,
  ): Record<keyof ShippingDimensions | typeof PICKUP_ONLY_CONTROL, unknown[]> {
    const integerPattern = Validators.pattern(/^\d+$/);
    const sideValidators = [Validators.required, Validators.min(1), Validators.max(SHIPPING_DIMENSION_MAX_CM), integerPattern];
    const weightValidators = [Validators.required, Validators.min(1), Validators.max(SHIPPING_WEIGHT_MAX_GRAMS), integerPattern];
    const isPickupOnly = this.hasZeroDimension(dimensions);

    return {
      weightGrams: [isPickupOnly ? null : dimensions?.weightGrams ?? null, weightValidators],
      lengthCm: [isPickupOnly ? null : dimensions?.lengthCm ?? null, sideValidators],
      widthCm: [isPickupOnly ? null : dimensions?.widthCm ?? null, sideValidators],
      heightCm: [isPickupOnly ? null : dimensions?.heightCm ?? null, sideValidators],
      [PICKUP_ONLY_CONTROL]: [isPickupOnly],
    };
  }

  private hasZeroDimension(dimensions: Partial<NullableShippingDimensions> | null): boolean {
    return SHIPPING_DIMENSION_FIELDS.some(({ key }) => dimensions?.[key] === 0);
  }

  /**
   * A dimension counts as missing whenever the size is not pickup-only, even if its control
   * ended up disabled: 0 is the backend's pickup-only marker, so a missing value must never be
   * sent as 0.
   */
  private getDimensionErrors(dimensionsGroup: FormGroup, prefix: string): string[] {
    if (this.isPickupOnly(dimensionsGroup)) {
      return [];
    }

    return SHIPPING_DIMENSION_FIELDS
      .filter(({ key }) => {
        const dimensionControl = dimensionsGroup.get(key);
        return Boolean(dimensionControl?.invalid) || !this.isPositiveInteger(dimensionControl?.value);
      })
      .map(({ key, label }) => {
        const maxValue = key === 'weightGrams' ? '1.000.000 g (1.000 kg)' : `${SHIPPING_DIMENSION_MAX_CM} cm`;
        return `${prefix}: el ${label} es requerido y debe ser un número entero entre 1 y ${maxValue}. Si no tiene despacho, marca "Solo retiro en tienda".`;
      });
  }

  private toShippingDimensions(rawDimensions: Record<keyof ShippingDimensions | typeof PICKUP_ONLY_CONTROL, unknown>): ShippingDimensions {
    if (rawDimensions[PICKUP_ONLY_CONTROL]) {
      return { weightGrams: 0, lengthCm: 0, widthCm: 0, heightCm: 0 };
    }

    return {
      weightGrams: Number(rawDimensions.weightGrams),
      lengthCm: Number(rawDimensions.lengthCm),
      widthCm: Number(rawDimensions.widthCm),
      heightCm: Number(rawDimensions.heightCm),
    };
  }

  private patchDimensionsGroup(dimensionsGroup: FormGroup, dimensions: Partial<NullableShippingDimensions> | null): void {
    const isPickupOnly = this.hasZeroDimension(dimensions);
    const emptyDimensions = { weightGrams: null, lengthCm: null, widthCm: null, heightCm: null };

    dimensionsGroup.patchValue(
      isPickupOnly
        ? { ...emptyDimensions, [PICKUP_ONLY_CONTROL]: true }
        : { ...emptyDimensions, ...dimensions, [PICKUP_ONLY_CONTROL]: false },
      { emitEvent: false },
    );
    this.applyPickupOnlyState(dimensionsGroup);
  }

  private hasIncompleteShippingDimensions(): boolean {
    const dimensionsGroups = this.useSizeVariations
      ? (this.sizesFormArray.controls as FormGroup[])
      : [this.baseDimensionsGroup];

    return dimensionsGroups.some((dimensionsGroup) => this.getDimensionErrors(dimensionsGroup, '').length > 0);
  }

  private isPositiveInteger(value: unknown): boolean {
    const numericValue = Number(value);
    return value !== null && value !== '' && Number.isInteger(numericValue) && numericValue > 0;
  }
}
